/* global SideDrawer */

class CartDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
    this.preloadCrossSells();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    this.discountSubmitHandler = this.handleDiscountSubmit.bind(this);
    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    this.addEventListener('submit', this.discountSubmitHandler);
    this.addEventListener('on:cart-drawer:before-open', () => {
      theme.manuallyLoadImages(this);
      this.initCrossSells();
    });
    this.addEventListener('on:cart:after-merge', () => {
      theme.manuallyLoadImages(this);
      this.initCrossSells();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    this.removeEventListener('submit', this.discountSubmitHandler);
    this.overlay?.classList.remove('overlay--cart-drawer');

    if (this.crossSellIdleCallbackId && window.cancelIdleCallback) {
      window.cancelIdleCallback(this.crossSellIdleCallbackId);
    } else if (this.crossSellPreloadTimeoutId) {
      clearTimeout(this.crossSellPreloadTimeoutId);
    }
  }

  preloadCrossSells() {
    const preload = () => {
      this.initCrossSells();
    };

    if (window.requestIdleCallback) {
      this.crossSellIdleCallbackId = window.requestIdleCallback(preload, { timeout: 1500 });
    } else {
      this.crossSellPreloadTimeoutId = setTimeout(preload, 600);
    }
  }

  initCrossSells() {
    const init = () => {
      this.querySelectorAll('cc-cart-cross-sell').forEach((el) => {
        if (typeof el.init === 'function') {
          el.init();
        }
      });
    };

    if (window.customElements && !customElements.get('cc-cart-cross-sell')) {
      customElements.whenDefined('cc-cart-cross-sell').then(init);
      return;
    }

    init();
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  open(...args) {
    this.overlay?.classList.add('overlay--cart-drawer');
    super.open(...args);
  }

  close(...args) {
    this.overlay?.classList.remove('overlay--cart-drawer');
    super.close(...args);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }

  async handleDiscountSubmit(evt) {
    const form = evt.target.closest('[data-cart-discount-form]');
    if (!form || !this.contains(form)) return;

    evt.preventDefault();

    const input = form.querySelector('[data-cart-discount-input]');
    const submit = form.querySelector('[data-cart-discount-submit]');
    if (!input || !submit) return;

    const code = input.value.trim();
    if (!code) {
      input.focus();
      return;
    }

    const requestId = (this.discountRequestId || 0) + 1;
    this.discountRequestId = requestId;
    this.setDiscountError(form);
    form.setAttribute('aria-busy', 'true');
    input.disabled = true;
    submit.disabled = true;

    try {
      const response = await fetch(theme.routes.cartUpdate, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ discount: code })
      });
      const cart = await response.json().catch(() => null);

      if (requestId !== this.discountRequestId) return;

      if (!response.ok || this.isRejectedDiscount(cart, code)) {
        this.setDiscountError(form, form.dataset.cartDiscountInvalid);
        return;
      }

      document.dispatchEvent(new CustomEvent('on:cart:change', {
        bubbles: true,
        cancelable: false
      }));
    } catch (error) {
      if (requestId === this.discountRequestId) {
        this.setDiscountError(form, form.dataset.cartDiscountInvalid);
      }
    } finally {
      if (requestId === this.discountRequestId) {
        form.removeAttribute('aria-busy');
        input.disabled = false;
        submit.disabled = false;
      }
    }
  }

  isRejectedDiscount(cart, code) {
    if (!Array.isArray(cart?.discount_codes)) return false;

    return cart.discount_codes.some((discount) => (
      String(discount.code || '').toLowerCase() === code.toLowerCase()
      && discount.applicable === false
    ));
  }

  setDiscountError(form, message = '') {
    const error = form.querySelector('[data-cart-discount-error]');
    if (!error) return;

    error.textContent = message;
    error.hidden = !message;

    if (message) {
      requestAnimationFrame(() => {
        error.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }
  }
}

window.customElements.define('cart-drawer', CartDrawer);
